import { Component, type ErrorInfo, type ReactNode } from 'react';

interface State {
  hasError: boolean;
  error: Error | null;
  info: ErrorInfo | null;
}

interface Props {
  children: ReactNode;
}

/**
 * Catches render-time exceptions in the widget tree and surfaces them
 * inline instead of leaving a blank page. Without this, a single
 * uncaught throw during App init produces an empty `<div id="root">`
 * and the user sees nothing — the worst possible failure mode for a
 * widget embedded in a third-party site.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, info: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, info: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ hasError: true, error, info });
    // Surface in console too so devtools / Sentry can pick up.
    console.error('[Crypto-pay widget] render error:', error, info);
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    const { error, info } = this.state;
    return (
      <div
        role="alert"
        style={{
          maxWidth: 560,
          margin: '40px auto',
          padding: 20,
          borderRadius: 12,
          background: '#1f1217',
          color: '#fda4af',
          border: '1px solid #be123c',
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontSize: 14,
          lineHeight: 1.5,
        }}
      >
        <h2 style={{ margin: '0 0 12px', fontSize: 16, color: '#fecaca' }}>
          Crypto-pay widget failed to start
        </h2>
        <p style={{ margin: '0 0 8px' }}>
          <strong>{error?.name ?? 'Error'}:</strong>{' '}
          <code style={{ background: 'transparent' }}>
            {error?.message ?? 'unknown error'}
          </code>
        </p>
        {error?.stack ? (
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: 'pointer', color: '#fecaca' }}>
              Stack trace
            </summary>
            <pre
              style={{
                marginTop: 8,
                padding: 12,
                background: '#0a0207',
                borderRadius: 8,
                overflowX: 'auto',
                color: '#fca5a5',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {error.stack}
            </pre>
          </details>
        ) : null}
        {info?.componentStack ? (
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: 'pointer', color: '#fecaca' }}>
              Component stack
            </summary>
            <pre
              style={{
                marginTop: 8,
                padding: 12,
                background: '#0a0207',
                borderRadius: 8,
                overflowX: 'auto',
                color: '#fca5a5',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {info.componentStack}
            </pre>
          </details>
        ) : null}
        <p
          style={{
            margin: '16px 0 0',
            fontSize: 12,
            color: '#fda4af',
            opacity: 0.8,
          }}
        >
          Share this error with the integrator at
          info@metaversetradingltduk.com.
        </p>
      </div>
    );
  }
}
